/**
 * Superall router — wouter route table.
 *
 * `/login` is public; every other route is wrapped in `<RequireAuth>`
 * which redirects unauthed users to `/login?from=<original-path>`. The
 * AppShell renders inside the protected routes; LoginPage is bare.
 */

import * as React from 'react';
import { Redirect, Route, Switch, useLocation } from 'wouter';
import { PageTransition } from '@workspace/ui';

import { AppShell } from './components/AppShell';
import { HomePage } from './pages/HomePage';
import { LauncherPage } from './pages/LauncherPage';
import { LoginPage } from './pages/LoginPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { LoyaltyPage } from './pages/LoyaltyPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { NotificationPrefsPage } from './pages/NotificationPrefsPage';
import { ProfilePage } from './pages/ProfilePage';
import { SettingsPage } from './pages/SettingsPage';
import { WalletPage } from './pages/WalletPage';
import { useAuth } from './providers/AuthProvider';

function RequireAuth({ children }: { children: React.ReactNode }): React.ReactElement {
  const { token, isLoading } = useAuth();
  const [location] = useLocation();

  if (isLoading) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex min-h-screen items-center justify-center text-sm text-neutral-500"
      >
        …
      </div>
    );
  }

  if (!token) {
    const from = encodeURIComponent(location || '/');
    return <Redirect to={`/login?from=${from}`} />;
  }
  return <AppShell>{children}</AppShell>;
}

function RouterInner(): React.ReactElement {
  const [location] = useLocation();
  return (
    <PageTransition pathKey={location} variant="up">
      <Switch>
        <Route path="/login" component={LoginPage} />
        {/* GAP-26: password recovery */}
        <Route path="/forgot-password" component={ForgotPasswordPage} />

        <Route path="/">
          {() => (
            <RequireAuth>
              <HomePage />
            </RequireAuth>
          )}
        </Route>

        <Route path="/launcher">
          {() => (
            <RequireAuth>
              <LauncherPage />
            </RequireAuth>
          )}
        </Route>

        <Route path="/wallet">
          {() => (
            <RequireAuth>
              <WalletPage />
            </RequireAuth>
          )}
        </Route>

        <Route path="/loyalty">
          {() => (
            <RequireAuth>
              <LoyaltyPage />
            </RequireAuth>
          )}
        </Route>

        <Route path="/notifications">
          {() => (
            <RequireAuth>
              <NotificationsPage />
            </RequireAuth>
          )}
        </Route>

        <Route path="/profile">
          {() => (
            <RequireAuth>
              <ProfilePage />
            </RequireAuth>
          )}
        </Route>

        <Route path="/settings">
          {() => (
            <RequireAuth>
              <SettingsPage />
            </RequireAuth>
          )}
        </Route>

        <Route path="/notifications/preferences">
          {() => (
            <RequireAuth>
              <NotificationPrefsPage />
            </RequireAuth>
          )}
        </Route>

        {/* Fallback — unknown route bounces home (auth-gated, so unauthed
          users get sent to /login from there). */}
        <Route>{() => <Redirect to="/" />}</Route>
      </Switch>
    </PageTransition>
  );
}

export function Router(): React.ReactElement {
  return <RouterInner />;
}
